import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./dialog";
import { Button } from "./button";

interface ConfirmDialogProps {
  open:          boolean;
  title:         string;
  description?:  string;
  confirmLabel?: string;
  cancelLabel?:  string;
  variant?:      "destructive" | "default";
  loading?:      boolean;
  onConfirm:     () => void;
  onCancel:      () => void;
  /** What the confirm acts on, shown under the description (e.g. the rows a fill would change). */
  children?:     ReactNode;
}

/**
 * Shared confirmation dialog - replaces `window.confirm()` everywhere across
 * web_admin / web_client / B2B (chapter 0d.4). The destructive variant shows a
 * red warning icon; the default one uses the primary colour.
 */
export function ConfirmDialog({
  open, title, description, confirmLabel, cancelLabel,
  variant = "destructive", loading = false, onConfirm, onCancel, children,
}: ConfirmDialogProps) {
  return (
    // While the confirm action runs, Escape, a click outside and the corner X
    // must not walk away from it: the Cancel button is already disabled, and a
    // dismissed dialog left the caller's request running with nothing on screen.
    // The X is hidden rather than left as a button that does nothing.
    <Dialog open={open} onOpenChange={(o) => { if (!o && !loading) onCancel(); }}>
      <DialogContent className="max-w-sm" hideClose={loading}>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            {variant === "destructive" && (
              <div className="shrink-0 size-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="size-5 text-destructive" />
              </div>
            )}
            <DialogTitle>{title}</DialogTitle>
          </div>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {children}

        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            {cancelLabel ?? "Cancel"}
          </Button>
          <Button
            variant={variant === "destructive" ? "destructive" : "primary"}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel ?? (variant === "destructive" ? "Delete" : "OK")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
